<template>
  <!-- <Toast></Toast> -->
  <div class="page">
    <div class="container">
      <div class="page-header">
        <h1>{{ isEditing ? 'Edit Subject' : 'New Subject' }}</h1>
        <router-link to="/bills-by-subject" class="back-btn">
          ← Back to Bills By Subject
        </router-link>
      </div>
      
      <div class="form-card">
        <form @submit.prevent="saveSubject" class="subject-form">
          <!-- Basic Information -->
          <div class="basic-info">
            <div class="form-row-four">
              <div class="form-group">
                <label>Period of Congress:</label>
                <Select v-model="form.period_of_congress" :options="congressNo" required />
              </div>
              <div class="form-group">
                <label>Type of Proposal:</label>
                <MultiSelect v-model="form.tier" :options="tierOptions" placeholder="Select Type" class="w-full" required/>
              </div>
              <div class="form-group">
                <label>Type of Bill:</label>
                <MultiSelect v-model="form.type" :options="subjectTypeOptions" placeholder="Select Subject Types" class="w-full" filter />
              </div>
              <div class="form-group">
                <label>Urgency:</label>
                <Select v-model="form.urgency" :options="urgencyOptions" required />
              </div>
            </div>
            <div class="form-group">
              <label>Subject Title:</label>
              <input v-model="form.title" type="text" required>
            </div>
          </div>

          <!-- Legislative Timeline -->
          <div class="status-display">
           
            <h3>Legislative Process Timeline</h3>
            <div class="timeline-container">
              <div class="timeline">
                <div v-for="(stage, index) in form.timeline" :key="index" 
                     :class="['timeline-item', { 
                       'completed': stage.data !== null, 
                      //  'current': stage.current,
                       'pending': stage.data === null
                     }]">
                  <div class="timeline-marker">
                    <i :class="stage.data !== null ? 'pi pi-check' : stage.data == null ? 'pi pi-clock' : 'pi pi-circle'"></i>
                  </div>
                  <div class="timeline-content">
                    <div class="timeline-title">{{ stage.title }}</div>
                    <div class="timeline-date">{{ stage.data?.status_date || '-' }}</div>
                    <div class="timeline-details">{{ stage.data?.status || 'Not started' }}</div>
                    <!-- <div class="timeline-status" v-if="stage.current">Current Stage</div> -->
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Tabs Section -->
          <div class="tabs-container">
            <div class="tabs-header">
              <button type="button" 
                      v-for="tab in tabs" 
                      :key="tab.id"
                      @click="activeTab = tab.id"
                      :class="['tab-btn', { active: activeTab === tab.id }]">
                {{ tab.name }}
              </button>
            </div>
            
            <div class="tab-content">
              <!-- House Tab -->
              <HouseTab 
                v-show="activeTab === 'house'"
                :houseData="form.house_data"
                :keyIssues="form.house_key_issues"
                :referrals="referrals"
                :authors="authors"
                :isSubButtonDisabled="isSubButtonDisabled('house')"
                :revenueYears="revenueYears"
                :positionPapers="form.position_papers"
                :houseActiveTab="houseActiveTab"
                @trackDeletedId="trackDeletedId"
                @addRevenueRow="addRevenueRow"
                @addBudgetaryRow="addBudgetaryRow"
                @addDofConcernsRow="addDofConcernsRow"
                @deleteRevenueRow="deleteRevenueRow"
                @deleteBudgetaryRow="deleteBudgetaryRow"
                @deleteDofConcernsRow="deleteDofConcernsRow"
                @getYearTotal="getYearTotal"
                @autoResize="autoResize"
                @addPositionPaper="addPositionPaper"
                @deletePositionPaper="deletePositionPaper"
              />

              <!-- Senate Tab -->
              <SenateTab 
                v-show="activeTab === 'senate'"
                :senateData="form.senate_data"
                :keyIssues="form.senate_key_issues"
                :referrals="referrals"
                :authors="authors"
                :isSubButtonDisabled="isSubButtonDisabled('senate')"
                :revenueYears="revenueYears"
                :positionPapers="form.position_papers"
                :senateActiveTab="senateActiveTab"
                @trackDeletedId="trackDeletedId"
                @addSenateRevenueRow="addSenateRevenueRow"
                @addSenateBudgetaryRow="addSenateBudgetaryRow"
                @addSenateDofConcernsRow="addSenateDofConcernsRow"
                @deleteSenateRevenueRow="deleteSenateRevenueRow"
                @deleteSenateBudgetaryRow="deleteSenateBudgetaryRow"
                @deleteSenateDofConcernsRow="deleteSenateDofConcernsRow"
                @getYearTotal="getYearTotal"
                @autoResize="autoResize"
                @addPositionPaper="addPositionPaper"
                @deletePositionPaper="deletePositionPaper"
              />

              <!-- Bicam Tab -->
              <BicamTab 
                v-show="activeTab === 'bicam'"
                :bicamBill="form.bicam_bill"
                :keyIssues="form.bicam_key_issues"
                :authors="authors"
                :revenueYears="revenueYears"
                :positionPapers="form.position_papers"
                @addBicamRevenueRow="addBicamRevenueRow"
                @addBicamBudgetaryRow="addBicamBudgetaryRow"
                @addBicamDofConcernsRow="addBicamDofConcernsRow"
                @deleteBicamRevenueRow="deleteBicamRevenueRow"
                @deleteBicamBudgetaryRow="deleteBicamBudgetaryRow"
                @deleteBicamDofConcernsRow="deleteBicamDofConcernsRow"
                @getYearTotal="getYearTotal"
                @autoResize="autoResize"
                @addPositionPaper="addPositionPaper"
                @deletePositionPaper="deletePositionPaper"
              />

              <!-- Enrollment Tab -->
              <EnrollmentTab 
                v-show="activeTab === 'enrollment'"
                :enrolledBill="form.enrolled_bill"
                :keyIssues="form.enrollment_key_issues"
                :authors="authors"
                :revenueYears="revenueYears"
                :positionPapers="form.position_papers"
                @trackDeletedId="trackDeletedId"
                @addEnrollmentRevenueRow="addEnrollmentRevenueRow"
                @addEnrollmentBudgetaryRow="addEnrollmentBudgetaryRow"
                @addEnrollmentDofConcernsRow="addEnrollmentDofConcernsRow"
                @deleteEnrollmentRevenueRow="deleteEnrollmentRevenueRow"
                @deleteEnrollmentBudgetaryRow="deleteEnrollmentBudgetaryRow"
                @deleteEnrollmentDofConcernsRow="deleteEnrollmentDofConcernsRow"
                @getYearTotal="getYearTotal"
                @autoResize="autoResize"
                @addPositionPaper="addPositionPaper"
                @deletePositionPaper="deletePositionPaper"
              />

              <!-- Enactment Tab -->
              <EnactmentTab 
                v-show="activeTab === 'enactment'"
                :enactmentBill="form.enactment_bill"
                :keyIssues="form.enactment_key_issues"
                :revenueYears="revenueYears"
                :positionPapers="form.position_papers"
                @trackDeletedId="trackDeletedId"
                @addEnactmentRevenueRow="addEnactmentRevenueRow"
                @addEnactmentBudgetaryRow="addEnactmentBudgetaryRow"
                @addEnactmentDofConcernsRow="addEnactmentDofConcernsRow"
                @deleteEnactmentRevenueRow="deleteEnactmentRevenueRow"
                @deleteEnactmentBudgetaryRow="deleteEnactmentBudgetaryRow"
                @deleteEnactmentDofConcernsRow="deleteEnactmentDofConcernsRow"
                @getYearTotal="getYearTotal"
                @autoResize="autoResize"
                @addPositionPaper="addPositionPaper"
                @deletePositionPaper="deletePositionPaper"
              />
            </div>
          </div>

          <!-- Referral Inputs Section -->
          <div class="referral-inputs-section">
            <h3>Referral Inputs</h3>
            <div class="referral-inputs-container">
              
              <div v-for="(referral, index) in form.referral_inputs" :key="index" class="referral-input-group">
                <div class="form-row-three">
                  <div class="form-group">
                    <label>Referred To:</label>
                    <Select v-model="referral.referred_to" :options="offices" optionLabel="name" optionValue="code" placeholder="Select office" class="w-full" />
                  </div>
                  <div class="form-group">
                    <label>Referral Received Date:</label>
                    <DatePicker v-model="referral.received_date" placeholder="Select date" @date-select="dateSelect" class="w-full" />
                  </div>
                  <div class="form-group">
                    <label>Action:</label>
                    <button type="button" @click="removeReferralInput(index)" class="remove-btn">
                      <i class="pi pi-trash"></i> Remove
                    </button>
                  </div>
                </div>
                <div class="form-group">
                  <label>Remarks:</label>
                  <textarea v-model="referral.pending_inputs" placeholder="Enter pending inputs" rows="3"></textarea>
                </div>
                <div class="form-group">
                  <label>Upload File:</label>
                  <div class="file-upload-container">
                    <FileUpload mode="basic" :auto="false" chooseLabel="Choose File" class="w-full" @select="onFileSelect($event, index)" />
                    <button v-if="referral.file_path" type="button" @click="viewFile(referral.file_path)" class="view-file-btn">
                      <i class="pi pi-eye"></i> View File
                    </button>
                  </div>
                  <small v-if="referral.fileName" class="selected-file">Selected: {{ referral.fileName }}</small>
                  <small v-if="referral.file_path && !referral.fileName" class="uploaded-file">Uploaded: {{ getFileName(referral.file_path) }}</small>
                </div>
              </div>
              <div v-if="form.referral_inputs.length === 0" class="no-data">
                No referral inputs added yet
              </div>
              <button type="button" @click="addReferralInput" class="add-referral-btn">
                <i class="pi pi-plus"></i> Add Referral Input
              </button>
            </div>
          </div>


          <!-- Additional Information -->
          <div class="addtional-info">
              <div class="form-group">
                <label>Background/Rationale:</label>
                <Textarea v-model="form.background"  rows="2"/>
              </div>
              <div class="form-group">
                <label>Current/Existing Regime</label>
                <Textarea v-model="form.regime" rows="2"/>
              </div>
              <div class="form-group">
                <label>Proposal/Salient Provisions</label>
                <Textarea v-model="form.proposal" rows="2"/>
              </div>
              <div class="form-group">
                <label>FPMG Recommended Positionsfp</label>
                <Textarea v-model="form.fpmg" rows="2"/>
              </div>
              <div class="form-group">
                <label>OSEC Instruction/Remarks</label>
                <Textarea v-model="form.osec" rows="2"/>
              </div>
          </div>

          <!-- Related Committee Meetings -->
          <div v-if="relatedMeetings.length > 0" class="related-meetings">
            <h3>Related Committee Meetings</h3>
            <div class="meetings-list">
              <div v-for="meeting in relatedMeetings" :key="meeting.id" class="meeting-item">
                <div class="meeting-header">
                  <h4>{{ meeting.committee_name }}</h4>
                  <span class="meeting-date">{{ formatDate(meeting.meeting_date) }}</span>
                </div>
                <div class="meeting-details">
                  <p><strong>Time:</strong> {{ meeting.meeting_time }}</p>
                  <p><strong>Venue:</strong> {{ meeting.venue }}</p>
                  <p v-if="meeting.committee"><strong>Committee:</strong> {{ meeting.committee }}</p>
                  <p v-if="meeting.status"><strong>Status:</strong> {{ meeting.status }}</p>
                  <p v-if="meeting.display_referrals"><strong>Referrals:</strong> {{ meeting.display_referrals }}</p>
                  <p v-if="meeting.agenda"><strong>Agenda:</strong> {{ meeting.agenda }}</p>
                  <p v-if="meeting.agenda_link"><strong>Agenda Link:</strong> <a :href="meeting.agenda_link" target="_blank" class="agenda-link">{{ meeting.agenda_link }}</a></p>
                </div>
              </div>
            </div>
          </div>


          <div class="form-actions">
            <router-link to="/bills-by-subject" class="cancel-btn">Cancel</router-link>
            <button v-if="isEditing" type="button" @click="printSubject" class="print-btn">
              <i class="pi pi-print"></i> Print Subject Details
            </button>
            <button v-if="isEditing" type="button" @click="printKeyIssues" class="print-btn key-issues-btn">
              <i class="pi pi-table"></i> Print Key Issues
            </button>
            <button type="submit" class="save-btn">{{ isEditing ? 'Update' : 'Save' }}</button>
          </div>
        </form>
      </div>
    </div>
    
    <!-- File Viewer Modal -->
    <Dialog v-model:visible="showFileModal" :header="fileName" modal :style="{ width: '90vw', height: '90vh' }">
      <div id="pdfViewer" style="width: 100%; height: 75vh;"></div>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import MultiSelect from 'primevue/multiselect'
import Select from 'primevue/select'
import DatePicker from 'primevue/datepicker'
import FileUpload from 'primevue/fileupload'
import Dialog from 'primevue/dialog'
import HouseTab from './HouseTab.vue'
import SenateTab from './SenateTab.vue'
import BicamTab from './BicamTab.vue'
import EnrollmentTab from './EnrollmentTab.vue'
import EnactmentTab from './EnactmentTab.vue'
import api from '../axios.js'
import Swal from 'sweetalert2'
import PDFObject from 'pdfobject'

import moment from 'moment-timezone';

moment.tz.setDefault('Asia/Taipei')

const dateSelect = () => {
  Date.prototype.toJSON = function(){ return moment(this).format(); }
    moment().tz("Asia/Taipei").format();
}
const route = useRoute()
const toast = useToast()


// Reactive data
const isEditing = ref(false)
const activeTab = ref('house')
const relatedMeetings = ref([])
const legislativeHistory = ref([])
const deletedIds = ref({
  legislative_status: [],
  position_papers: [],
  impact_records: [],
  dof_concerns: [],
  referral_inputs: [],
  bills: [],
})
const revenueStartYear = new Date().getFullYear()
const revenueEndYear = new Date().getFullYear() + 5
const referrals = ref([])
const authors = ref([])
const offices = ref([])
const showFileModal = ref(false)
const fileName = ref('')

// Static data
const subjectTypeOptions = ['RED Bill', 'DOF Priority', 'LEDAC']
const tierOptions = ['Tax', 'Budgetary', 'Other DOF Concerns']
const congressNo = ['20th Congress', '19th Congress', '18th Congress', '17th Congress']
const urgencyOptions = ['High', 'Medium', 'Low']
const tabs = [
  { id: 'house', name: 'House of Representatives' },
  { id: 'senate', name: 'Senate' },
  { id: 'bicam', name: 'Bicameral Conference' },
  { id: 'enrollment', name: 'Enrollment' },
  { id: 'enactment', name: 'Enactment' }
]

// Helper functions
const createDefaultRevenueRow = () => {
  const row = { particulars: '', type: 'house' }
  for (let year = revenueStartYear; year <= revenueEndYear; year++) {
    row[year.toString()] = null
  }
  return row
}

// Form data
const form = ref({
  id: null,
  title: '',
  period_of_congress: '',
  tier: '',
  type: [],
  urgency: '',
  timeline: [],
  position_papers: [],
  referral_inputs: [],
  house_key_issues: {
    revenue_rows: [createDefaultRevenueRow()],
    budgetary_rows: [createDefaultRevenueRow()],
    dof_concerns_rows: [{ concerns: '', type: 'house' }]
  },
  senate_key_issues: {
    revenue_rows: [createDefaultRevenueRow()],
    budgetary_rows: [createDefaultRevenueRow()],
    dof_concerns_rows: [{ concerns: '', type: 'senate' }]
  },
  bicam_key_issues: {
    revenue_rows: [createDefaultRevenueRow()],
    budgetary_rows: [createDefaultRevenueRow()],
    dof_concerns_rows: [{ concerns: '', type: 'bicam' }]
  },
  enrollment_key_issues: {
    revenue_rows: [createDefaultRevenueRow()],
    budgetary_rows: [createDefaultRevenueRow()],
    dof_concerns_rows: [{ concerns: '', type: 'enrollment' }]
  },
  enactment_key_issues: {
    revenue_rows: [createDefaultRevenueRow()],
    budgetary_rows: [createDefaultRevenueRow()],
    dof_concerns_rows: [{ concerns: '', type: 'enactment' }]
  },
  bicam_bill: {
    title: '',
    short_title: '',
    nos: '',
    authors: '',
    remarks: '',
    source: '',
    legisstatus: []
  },
  enrolled_bill: {
    title: '',
    short_title: '',
    nos: '',
    authors: '',
    remarks: '',
    source: '',
    legisstatus: [],
    positionpaper: {
      positiontitle: '',
      status: '',
      date: '',
      source: '',
      type: 'enrolled'
    }
  },
  enactment_bill: {
    title: '',
    short_title: '',
    nos: '',
    remarks: '',
    source: '',
    legisstatus: []
  },
  house_data: {
    resolutions: [],
    firstreading: [],
    unnumbered: [],
    committee: {
      billtitle: '',
      shorttitle: '',
      no: '',
      authors: '',
      remarks: '',
      source: '',
      salient_features: '',
      legisstatus: []
    },
    thirdreading: {
      billtitle: '',
      shorttitle: '',
      no: '',
      authors: '',
      remarks: '',
      link: '',
      type: 'house',
      revenueimpacts: [],
      salient_features: ''
    }
  },
  senate_data: {
    resolutions: [],
    firstreading: [],
    unnumbered: [],
    committee: {
      billtitle: '',
      shorttitle: '',
      no: '',
      authors: '',
      remarks: '',
      source: '',
      salient_features: '',
      legisstatus: []
    },
    thirdreading: {
      billtitle: '',
      shorttitle: '',
      no: '',
      authors: '',
      remarks: '',
      link: '',
      type: 'senate',
      positionpapers: [],
      revenueimpacts: [],
      salient_features: ''
    }
  },
  background: '',
  regime: '',
  proposal: '',
  fpmg: '',
  osec: ''
})

// Computed properties
const revenueYears = computed(() => {
  const years = []
  for (let year = revenueStartYear; year <= revenueEndYear; year++) {
    years.push(year.toString())
  }
  return years
})

const timelineStages = computed(() => {
  const houseHistory = legislativeHistory.value.filter(h => h.chamber === 'House')
  const senateHistory = legislativeHistory.value.filter(h => h.chamber === 'Senate')
  
  const stages = [
    { id: 'house', title: 'House of Representatives', chamber: 'House' },
    { id: 'senate', title: 'Senate', chamber: 'Senate' },
    { id: 'bicameral_conference', title: 'Bicameral Conference', chamber: 'Bicameral Conference', sub_type: null },
    { id: 'enrollment', title: 'Enrollment', chamber: 'Enrollment', sub_type: null },
    { id: 'enactment', title: 'Enactment', chamber: 'Enactment', sub_type: null }
  ]
  
  return stages.map((stage) => {
    let historyItem = null
    
    if (stage.chamber === 'House' && houseHistory.length > 0) {
      historyItem = houseHistory[houseHistory.length - 1]
    } else if (stage.chamber === 'Senate' && senateHistory.length > 0) {
      historyItem = senateHistory[senateHistory.length - 1]
    } else {
      historyItem = legislativeHistory.value.find(h => 
        h.chamber === stage.chamber
      )
    }
    
    const completed = !!historyItem
    const current = getCurrentStage() === stage.id
    
    return {
      ...stage,
      completed,
      current,
      date: historyItem?.created_at || null,
      details: historyItem?.sub_type || null
    }
  })
})

// Lifecycle
onMounted(async () => {
  await loadReferrals()
 
  await loadOffices()
  const id = route.params.id
  if (id) {
    isEditing.value = true
    await loadSubject(id)
  } else {
    legislativeHistory.value = []
  }
})

// Methods
const loadReferrals = async (chamber = 'House') => {
  try {
    const url = `/committees`
    const response = await api.get(url)
    referrals.value = response.data.map(r => r.name)
  } catch (error) {
    console.error('Error loading referrals:', error)
  }
}

const loadAuthors = async (no) => {
  
  try {
      const congno = no.replace(/(\d+)(st|nd|rd|th)/g, "$1") // Keeps only the digits
      .replace(/Congress?/g, "")            // Removes the words
      .replace(/\s+/g, " ")                 // Cleans up extra spaces
      .trim();

      const [senatorsRes, repsRes] = await Promise.all([
        api.get('/senators/'+congno+'/list'),
        api.get('/representatives/'+congno+'/list')
      ])
      authors.value = [
        ...senatorsRes.data.map(s => s.name),
        ...repsRes.data.map(r => r.name)
      ].sort()
    // }
  } catch (error) {
    console.error('Error loading authors:', error)
  }
}

const loadOffices = async () => {
  try {
    const response = await api.get('/offices')
    offices.value = response.data.map(office => ({
      name: office.name,
      code: office.code
    }))
  } catch (error) {
    console.error('Error loading offices:', error)
  }
}

const loadSubject = async (id) => {
  try {
    const response = await api.get(`/bills-by-subject/${id}`)
    const data = response.data
    await loadRelatedMeetings(id)
    await loadLegislativeHistory(id)
    await loadAuthors(data.period_of_congress);
    
    form.value.id = data.id
    form.value.title = data.subject_title || ''
    form.value.period_of_congress = data.period_of_congress || ''
    form.value.tier = data.tier || ''
    form.value.type = Array.isArray(data.subject_type) ? data.subject_type : (data.subject_type ? [data.subject_type] : [])
    form.value.urgency = data.urgency || ''
    form.value.position_papers = data.position_papers || []
    form.value.referral_inputs = data.referral_inputs || []
    form.value.house_data = {
      resolutions: data.resolutions || [],
      firstreading: data.firstreading || [],
      unnumbered: data.unnumbered || [],
      committee: data.committee || form.value.house_data.committee,
      thirdreading: data.thirdreading || form.value.house_data.thirdreading
    }
    form.value.senate_data = {
      resolutions: data.senate_resolutions || [],
      firstreading: data.senate_firstreading || [],
      unnumbered: data.senate_unnumbered || [],
      committee: data.senate_committee || form.value.senate_data.committee,
      thirdreading: data.senate_thirdreading || form.value.senate_data.thirdreading
    }
    form.value.bicam_bill = data.bicam_bill || form.value.bicam_bill
    form.value.enrolled_bill = data.enrolled_bill || form.value.enrolled_bill
    form.value.enactment_bill = data.enactment_bill || form.value.enactment_bill
    form.value.house_key_issues = data.house_key_issues || form.value.house_key_issues
    form.value.senate_key_issues = data.senate_key_issues || form.value.senate_key_issues
    form.value.bicam_key_issues = data.bicam_key_issues || form.value.bicam_key_issues
    form.value.enrollment_key_issues = data.enrollment_key_issues || form.value.enrollment_key_issues
    form.value.enactment_key_issues = data.enactment_key_issues || form.value.enactment_key_issues
    form.value.background = data.background
    form.value.regime = data.regime
    form.value.proposal = data.proposal
    form.value.fpmg = data.fpmg
    form.value.osec = data.osec

    form.value.timeline = data.timeline ? Object.values(data.timeline) : []

  } catch (error) {
    console.error('Error loading subject:', error)
  }
}
 

const loadRelatedMeetings = async (subjectId) => {
  try {
    const response = await api.get(`/committee-meetings/${subjectId}/related-meetings`)
    relatedMeetings.value = response.data
  } catch (error) {
    console.error('Error loading related meetings:', error)
    relatedMeetings.value = []
  }
}

const loadLegislativeHistory = async (subjectId) => {
  try {
    const response = await api.get(`/bills-by-subject/${subjectId}/legislative-history`)
    legislativeHistory.value = response.data
    setActiveTabFromTimeline()
  } catch (error) {
    console.error('Error loading legislative history:', error)
    legislativeHistory.value = []
  }

  // console.log('Legislative History:', legislativeHistory.value)
}

const formatDate = (dateString) => {
  if (!dateString) return 'No date'
  const date = new Date(dateString)
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  const month = months[date.getMonth()]
  const day = date.getDate()
  const year = date.getFullYear()
  let hours = date.getHours()
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12
  hours = hours ? hours : 12
  return `${month} ${day} ${year} ${hours}:${minutes} ${ampm}`
}

const formatTime = (timeString) => {
  if (!timeString) return ''
  const [hours, minutes] = timeString.split(':')
  const hour = parseInt(hours)
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour % 12 || 12
  return `${displayHour}:${minutes} ${ampm}`
}

const printSubject = async () => {
  try {
    // const response = await api.get(`/bills-by-subject/${form.value.id}/print`)
   
    const response = await api.get(`/bills-by-subject/${form.value.id}/print`, { responseType: 'blob' })
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
    window.open(url, '_blank')
  } catch (error) {
    console.error('Error printing subject:', error)
  }
}

const printKeyIssues = async () => {
  try {
    const response = await api.get(`/bills-by-subject/${form.value.id}/print-key-issues`, { responseType: 'blob' })
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
    window.open(url, '_blank')
  } catch (error) {
    console.error('Error printing key issues:', error)
  }
}

const saveSubject = async () => {
  const result = await Swal.fire({
    title: 'Confirm Save',
    text: `Are you sure you want to ${isEditing.value ? 'update' : 'save'} this subject?`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#1e3a8a',
    cancelButtonColor: '#6b7280',
    confirmButtonText: `Yes, ${isEditing.value ? 'update' : 'save'} it!`
  })

  if (!result.isConfirmed) return

  try {
    const payload = {
      title: form.value.title,
      period_of_congress: form.value.period_of_congress,
      tier: form.value.tier,
      type: form.value.type,
      urgency: form.value.urgency,
      position_papers: form.value.position_papers,
      referral_inputs: form.value.referral_inputs.map(r => ({ ...r, file: undefined })),
      resolutions: form.value.house_data.resolutions,
      firstreading: form.value.house_data.firstreading,
      unnumbered: form.value.house_data.unnumbered,
      committee: form.value.house_data.committee,
      thirdreading: form.value.house_data.thirdreading,
      house_key_issues: form.value.house_key_issues,
      senate_key_issues: form.value.senate_key_issues,
      bicam_key_issues: form.value.bicam_key_issues,
      enrollment_key_issues: form.value.enrollment_key_issues,
      enactment_key_issues: form.value.enactment_key_issues,
      senate_resolutions: form.value.senate_data.resolutions,
      senate_firstreading: form.value.senate_data.firstreading,
      senate_unnumbered: form.value.senate_data.unnumbered,
      senate_committee: form.value.senate_data.committee,
      senate_thirdreading: form.value.senate_data.thirdreading,
      bicam_bill: form.value.bicam_bill,
      enrolled_bill: form.value.enrolled_bill,
      enactment_bill: form.value.enactment_bill,
      deleted_ids: deletedIds.value,
      background: form.value.background,
      regime: form.value.regime,
      proposal: form.value.proposal,
      fpmg: form.value.fpmg,
      osec: form.value.osec,

    }
    
    const formData = new FormData()
    
    // Add JSON data
    Object.keys(payload).forEach(key => {
      formData.append(key, typeof payload[key] === 'object' ? JSON.stringify(payload[key]) : payload[key])
    })
    
    // Add files from referral inputs
    form.value.referral_inputs.forEach((referral, index) => {
      if (referral.file && referral.file instanceof File) {
        console.log(`Adding file for referral ${index}:`, referral.file.name)
        formData.append(`referral_file_${index}`, referral.file)
      }
    })
    
    // Debug FormData contents
    console.log('FormData entries:')
    for (let [key, value] of formData.entries()) {
      console.log(key, ':', value instanceof File ? `File: ${value.name}` : value)
    }

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }

    if (isEditing.value) {
      formData.append('_method', 'PUT')
      await api.post(`/bills-by-subject/${form.value.id}`, formData, config)
    } else { 
      const res = await api.post('/bills-by-subject', formData, config)
      window.location.href='/subject/edit/'+res.data.id;
    }

    await Swal.fire({
      title: 'Success!',
      text: `Subject ${isEditing.value ? 'updated' : 'saved'} successfully!`,
      icon: 'success',
      confirmButtonColor: '#10b981'
    })

    if (isEditing.value) {
      location.reload()
    }
  } catch (error) {
    console.error('Error saving subject:', error)
    await Swal.fire({
      title: 'Error!',
      text: `Failed to ${isEditing.value ? 'update' : 'save'} subject.`,
      icon: 'error',
      confirmButtonColor: '#dc2626'
    })
  }
}

const addPositionPaper = (type, subType = null) => {
  form.value.position_papers.push({
    positiontitle: '',
    status: '',
    date: '',
    source: '',
    type: type,
    sub_type: subType
  })
}

const deletePositionPaper = (type, subType, index) => {
  const filtered = form.value.position_papers.filter(p => p.type === type && (subType ? p.sub_type === subType : true))
  const paper = filtered[index]
  if (paper && paper.id) {
    deletedIds.value.position_papers.push(paper.id)
  }
  const paperIndex = form.value.position_papers.indexOf(paper)
  if (paperIndex > -1) {
    form.value.position_papers.splice(paperIndex, 1)
  }
}

// Utility functions
const getCurrentStage = () => {
  if (!legislativeHistory.value.length) return 'house'
  
  const houseHistory = legislativeHistory.value.filter(h => h.chamber === 'House')
  const senateHistory = legislativeHistory.value.filter(h => h.chamber === 'Senate')
  const bicamHistory = legislativeHistory.value.find(h => h.chamber === 'Bicameral Conference')
  const enrollmentHistory = legislativeHistory.value.find(h => h.chamber === 'Enrollment')
  const enactmentHistory = legislativeHistory.value.find(h => h.chamber === 'Enactment')
  
  if (enactmentHistory) return 'enactment'
  if (enrollmentHistory) return 'enrollment'
  if (bicamHistory) return 'bicam'
  if (senateHistory.length > 0) return 'senate'
  return 'house'
}

const getHouseActiveTab = () => {
  const houseHistory = legislativeHistory.value.filter(h => h.chamber === 'House')
  if (!houseHistory.length) return '0'
  const latestHouse = houseHistory[houseHistory.length - 1]
  const subType = latestHouse.sub_type
  if (subType === 'Bills on Third Reading') return '4'
  if (subType === 'Committee Report / Substitute Bill') return '3'
  if (subType === 'Unnumbered Substitute Bill') return '2'
  if (subType === 'Bills on First Reading') return '1'
  if (subType === 'Resolutions') return '0'

  return '0'
}

const getSenateActiveTab = () => {
  const senateHistory = legislativeHistory.value.filter(h => h.chamber === 'Senate')
  if (!senateHistory.length) return '0'
  const latestSenate = senateHistory[senateHistory.length - 1]
  const subType = latestSenate.sub_type
  if (subType === 'Bills on Third Reading') return '4'
  if (subType === 'Committee Report / Substitute Bill') return '3'
  if (subType === 'Unnumbered Substitute Bill') return '2'
  if (subType === 'Bills on First Reading') return '1'
  if (subType === 'Resolutions') return '0'

  return '0'
}

const setActiveTabFromTimeline = () => {
  activeTab.value = getCurrentStage()
}

const houseActiveTab = computed(() => getHouseActiveTab())
const senateActiveTab = computed(() => getSenateActiveTab())


const isSubButtonDisabled = () => false
const trackDeletedId = (type, id) => {
  if (id && !deletedIds.value[type].includes(id)) {
    deletedIds.value[type].push(id)
  }
}
const getYearTotal = () => '0.00'
const autoResize = (event) => {
  const textarea = event.target
  textarea.style.height = 'auto'
  textarea.style.height = textarea.scrollHeight + 'px'
}

// Key Issues methods
const addRevenueRow = () => {
  form.value.house_key_issues.revenue_rows.push(createDefaultRevenueRow())
}
const addBudgetaryRow = () => {
  form.value.house_key_issues.budgetary_rows.push(createDefaultRevenueRow())
}
const addDofConcernsRow = () => {
  form.value.house_key_issues.dof_concerns_rows.push({ concerns: '', type: 'house' })
}
const addSenateRevenueRow = () => {
  form.value.senate_key_issues.revenue_rows.push(createDefaultRevenueRow())
}
const addSenateBudgetaryRow = () => {
  form.value.senate_key_issues.budgetary_rows.push(createDefaultRevenueRow())
}
const addSenateDofConcernsRow = () => {
  form.value.senate_key_issues.dof_concerns_rows.push({ concerns: '', type: 'senate' })
}
const addBicamRevenueRow = () => {
  form.value.bicam_key_issues.revenue_rows.push(createDefaultRevenueRow())
}
const addBicamBudgetaryRow = () => {
  form.value.bicam_key_issues.budgetary_rows.push(createDefaultRevenueRow())
}
const addBicamDofConcernsRow = () => {
  form.value.bicam_key_issues.dof_concerns_rows.push({ concerns: '', type: 'bicam' })
}
const addEnrollmentRevenueRow = () => {
  form.value.enrollment_key_issues.revenue_rows.push(createDefaultRevenueRow())
}
const addEnrollmentBudgetaryRow = () => {
  form.value.enrollment_key_issues.budgetary_rows.push(createDefaultRevenueRow())
}
const addEnrollmentDofConcernsRow = () => {
  form.value.enrollment_key_issues.dof_concerns_rows.push({ concerns: '', type: 'enrollment' })
}
const addEnactmentRevenueRow = () => {
  form.value.enactment_key_issues.revenue_rows.push(createDefaultRevenueRow())
}
const addEnactmentBudgetaryRow = () => {
  form.value.enactment_key_issues.budgetary_rows.push(createDefaultRevenueRow())
}
const addEnactmentDofConcernsRow = () => {
  form.value.enactment_key_issues.dof_concerns_rows.push({ concerns: '', type: 'enactment' })
}

// Senate delete methods
const deleteSenateRevenueRow = (index) => {
  const row = form.value.senate_key_issues.revenue_rows[index]
  if (row && row.id) {
    deletedIds.value.impact_records.push(row.id)
  }
  form.value.senate_key_issues.revenue_rows.splice(index, 1)
}
const deleteSenateBudgetaryRow = (index) => {
  const row = form.value.senate_key_issues.budgetary_rows[index]
  if (row && row.id) {
    deletedIds.value.impact_records.push(row.id)
  }
  form.value.senate_key_issues.budgetary_rows.splice(index, 1)
}
const deleteSenateDofConcernsRow = (index) => {
  const row = form.value.senate_key_issues.dof_concerns_rows[index]
  if (row && row.id) {
    deletedIds.value.dof_concerns.push(row.id)
  }
  form.value.senate_key_issues.dof_concerns_rows.splice(index, 1)
}

// Bicam delete methods
const deleteBicamRevenueRow = (index) => {
  const row = form.value.bicam_key_issues.revenue_rows[index]
  if (row && row.id) {
    deletedIds.value.impact_records.push(row.id)
  }
  form.value.bicam_key_issues.revenue_rows.splice(index, 1)
}
const deleteBicamBudgetaryRow = (index) => {
  const row = form.value.bicam_key_issues.budgetary_rows[index]
  if (row && row.id) {
    deletedIds.value.impact_records.push(row.id)
  }
  form.value.bicam_key_issues.budgetary_rows.splice(index, 1)
}
const deleteBicamDofConcernsRow = (index) => {
  const row = form.value.bicam_key_issues.dof_concerns_rows[index]
  if (row && row.id) {
    deletedIds.value.dof_concerns.push(row.id)
  }
  form.value.bicam_key_issues.dof_concerns_rows.splice(index, 1)
}

// Enrollment delete methods
const deleteEnrollmentRevenueRow = (index) => {
  const row = form.value.enrollment_key_issues.revenue_rows[index]
  if (row && row.id) {
    deletedIds.value.impact_records.push(row.id)
  }
  form.value.enrollment_key_issues.revenue_rows.splice(index, 1)
}
const deleteEnrollmentBudgetaryRow = (index) => {
  const row = form.value.enrollment_key_issues.budgetary_rows[index]
  if (row && row.id) {
    deletedIds.value.impact_records.push(row.id)
  }
  form.value.enrollment_key_issues.budgetary_rows.splice(index, 1)
}
const deleteEnrollmentDofConcernsRow = (index) => {
  const row = form.value.enrollment_key_issues.dof_concerns_rows[index]
  if (row && row.id) {
    deletedIds.value.dof_concerns.push(row.id)
  }
  form.value.enrollment_key_issues.dof_concerns_rows.splice(index, 1)
}

// Enactment delete methods
const deleteEnactmentRevenueRow = (index) => {
  const row = form.value.enactment_key_issues.revenue_rows[index]
  if (row && row.id) {
    deletedIds.value.impact_records.push(row.id)
  }
  form.value.enactment_key_issues.revenue_rows.splice(index, 1)
}
const deleteEnactmentBudgetaryRow = (index) => {
  const row = form.value.enactment_key_issues.budgetary_rows[index]
  if (row && row.id) {
    deletedIds.value.impact_records.push(row.id)
  }
  form.value.enactment_key_issues.budgetary_rows.splice(index, 1)
}
const deleteEnactmentDofConcernsRow = (index) => {
  const row = form.value.enactment_key_issues.dof_concerns_rows[index]
  if (row && row.id) {
    deletedIds.value.dof_concerns.push(row.id)
  }
  form.value.enactment_key_issues.dof_concerns_rows.splice(index, 1)
}

// Delete methods for Key Issues
const deleteRevenueRow = (index) => {
  const row = form.value.house_key_issues.revenue_rows[index]
  if (row && row.id) {
    deletedIds.value.impact_records.push(row.id)
  }
  form.value.house_key_issues.revenue_rows.splice(index, 1)
}
const deleteBudgetaryRow = (index) => {
  const row = form.value.house_key_issues.budgetary_rows[index]
  if (row && row.id) {
    deletedIds.value.impact_records.push(row.id)
  }
  form.value.house_key_issues.budgetary_rows.splice(index, 1)
}
const deleteDofConcernsRow = (index) => {
  const row = form.value.house_key_issues.dof_concerns_rows[index]
  if (row && row.id) {
    deletedIds.value.dof_concerns.push(row.id)
  }
  form.value.house_key_issues.dof_concerns_rows.splice(index, 1)
}

// Referral inputs methods
const addReferralInput = () => {
  form.value.referral_inputs.push({
    referred_to: '',
    pending_inputs: '',
    received_date: null,
    fileName: null,
    file: null
  })
}

const onFileSelect = (event, index) => {
  const files = event.files || event.target.files
  const file = files[0]
  if (file) {
    form.value.referral_inputs[index].fileName = file.name
    form.value.referral_inputs[index].file = file
    console.log('File selected:', file.name, 'Type:', typeof file, 'Instance:', file instanceof File)
  }
}

const removeReferralInput = (index) => {
  const referral = form.value.referral_inputs[index]
  if (referral && referral.id) {
    deletedIds.value.referral_inputs = deletedIds.value.referral_inputs || []
    deletedIds.value.referral_inputs.push(referral.id)
  }
  form.value.referral_inputs.splice(index, 1)
}

const viewFile = async (filePath) => {
  try {
    console.log('Viewing file:', filePath)
    fileName.value = getFileName(filePath)
    showFileModal.value = true
    
    const response = await api.get(`/referral-files/view/${encodeURIComponent(filePath)}`, {
      responseType: 'blob'
    })
    
    console.log('Response received:', response)
    const blobUrl = URL.createObjectURL(response.data)
    console.log('Blob URL created:', blobUrl)
    
    setTimeout(() => {
      PDFObject.embed(blobUrl, '#pdfViewer')
    }, 100)
  } catch (error) {
    console.error('Error viewing file:', error)
    console.error('Error details:', error.response?.data)
    console.error('Error status:', error.response?.status)
  }
}

const getFileName = (filePath) => {
  return filePath ? filePath.split('/').pop() : ''
}
</script>

<style scoped>
.page {
  padding: 1rem;
  background: var(--p-surface-50);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.container {
  backdrop-filter: blur(10px);
  border-radius: 20px;
  margin: 1rem;
  padding: 1.5rem;
  width: calc(100vw - 2rem);
  max-width: none;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #1e3a8a;
}

.page-header h1 {
  color: #1e3a8a;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
}

.back-btn {
  background: #6b7280;
  color: white;
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s;
}

.back-btn:hover {
  background: #4b5563;
  transform: translateY(-1px);
}

.form-card {
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(30, 58, 138, 0.12);
  padding: 2rem;
  border-left: 4px solid #fbbf24;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.subject-form {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  height: 100%;
}

.basic-info {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid #e5e7eb;
}

.form-row-four {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #1e3a8a;
  font-size: 0.9rem;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.3s;
}

.form-group :deep(.p-multiselect) {
  height: 48px;
  min-height: 48px;
}

.form-group :deep(.p-multiselect .p-multiselect-label) {
  padding: 0.75rem;
  font-size: 0.9rem;
  line-height: 1.2;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #1e3a8a;
  box-shadow: 0 0 0 3px rgba(30, 58, 138, 0.1);
}

.tabs-container {
  margin-top: 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.tabs-header {
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 1.5rem;
  overflow-x: auto;
  position: relative;
}

.tab-btn {
  background: none;
  border: none;
  padding: 1rem 1.5rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.3s;
  border-bottom: 3px solid transparent;
  white-space: nowrap;
  font-size: 0.9rem;
}

.tab-btn:hover {
  color: #1e3a8a;
  background: #f8fafc;
}

.tab-btn.active {
  color: #1e3a8a;
  border-bottom-color: #fbbf24;
  background: #f8fafc;
}

.tab-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.status-display {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px;
  padding: 1.5rem;
  border: 2px solid #e5e7eb;
  margin-bottom: 1rem;
}

.status-display h3 {
  color: #1e3a8a;
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 3px solid #fbbf24;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.timeline-container {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px;
  padding: 1.5rem;
  border: 2px solid #e5e7eb;
}

.timeline {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  width: 100%;
}

.timeline::before {
  content: '';
  position: absolute;
  top: 1rem;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(to right, #e5e7eb 0%, #1e3a8a 50%, #e5e7eb 100%);
}

.timeline-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 120px;
}

.timeline-marker {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  z-index: 2;
  transition: all 0.3s ease;
  margin-bottom: 0.4rem;
  flex-shrink: 0;
}

.timeline-item.completed .timeline-marker {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 3px 8px rgba(16, 185, 129, 0.3);
}

.timeline-item.current .timeline-marker {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  box-shadow: 0 3px 8px rgba(245, 158, 11, 0.3);
  animation: pulse 2s infinite;
}

.timeline-item.pending .timeline-marker {
  background: #f3f4f6;
  color: #9ca3af;
  border: 2px solid #e5e7eb;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.timeline-content {
  background: white;
  border-radius: 6px;
  padding: 0.6rem;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  text-align: center;
  min-height: 70px;
  width: 100%;
  flex: 1;
}

.timeline-item.completed .timeline-content {
  border-left: 4px solid #10b981;
  background: linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%);
}

.timeline-item.current .timeline-content {
  border-left: 4px solid #f59e0b;
  background: linear-gradient(135deg, #ffffff 0%, #fffbeb 100%);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.1);
}

.timeline-item.pending .timeline-content {
  border-left: 4px solid #e5e7eb;
  background: #f9fafb;
}

.timeline-title {
  font-weight: 600;
  color: #1e3a8a;
  font-size: 0.8rem;
  margin-bottom: 0.2rem;
  line-height: 1.1;
}

.timeline-date {
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.2rem;
}

.timeline-details {
  font-size: 0.75rem;
  color: #6b7280;
  font-style: italic;
  margin-bottom: 0.2rem;
}

.timeline-status {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #f59e0b;
  background: #fffbeb;
  padding: 0.2rem 0.4rem;
  border-radius: 8px;
  display: inline-block;
}

.related-meetings {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 12px;
  padding: 1.5rem;
  border: 2px solid #0ea5e9;
  margin-top: 2rem;
  margin-bottom: 1rem;
}

.related-meetings h3 {
  color: #0c4a6e;
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 3px solid #0ea5e9;
}

.meetings-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.meeting-item {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e0f2fe;
  box-shadow: 0 2px 4px rgba(14, 165, 233, 0.1);
}

.meeting-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e0f2fe;
}

.meeting-header h4 {
  color: #0c4a6e;
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
}

.meeting-date {
  background: #0ea5e9;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.meeting-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.meeting-details p {
  margin: 0;
  font-size: 0.9rem;
  color: #374151;
}

.meeting-details strong {
  color: #0c4a6e;
  font-weight: 600;
}

.agenda-link {
  color: #0ea5e9;
  text-decoration: underline;
  word-break: break-all;
}

.agenda-link:hover {
  color: #0284c7;
}

.referral-inputs-section {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px;
  padding: 1.5rem;
  border: 2px solid #e5e7eb;
  /* margin-top: 2rem; */
  margin-bottom: 1rem;
}

.referral-inputs-section h3 {
  color: #1e3a8a;
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 3px solid #fbbf24;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.referral-inputs-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.referral-input-group {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.form-row-three {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 1rem;
  margin-bottom: 1rem;
}

.remove-btn {
  background: #dc2626;
  color: white;
  border: none;
  padding: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-btn:hover {
  background: #b91c1c;
  transform: scale(1.05);
}

.no-data {
  text-align: center;
  color: #6b7280;
  font-style: italic;
  padding: 2rem;
}

.add-referral-btn {
  background: #f59e0b;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1rem;
  transition: all 0.3s;
}

.add-referral-btn:hover {
  background: #d97706;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.file-upload-container {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.view-file-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8rem;
}

.view-file-btn:hover {
  background: #2563eb;
  transform: scale(1.05);
}

.selected-file, .uploaded-file {
  color: #059669;
  font-weight: 500;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.cancel-btn, .save-btn {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cancel-btn {
  background: #6b7280;
  color: white;
}

.cancel-btn:hover {
  background: #4b5563;
  transform: translateY(-1px);
}

.print-btn {
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
}

.print-btn:hover {
  background: linear-gradient(135deg, #047857 0%, #059669 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(5, 150, 105, 0.4);
}

.key-issues-btn {
  background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%);
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);
}

.key-issues-btn:hover {
  background: linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%);
  box-shadow: 0 6px 20px rgba(124, 58, 237, 0.4);
}

.save-btn {
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(30, 58, 138, 0.3);
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(30, 58, 138, 0.4);
}

@media (max-width: 768px) {
  .form-row-four {
    grid-template-columns: 1fr;
  }
  
  .page-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .tabs-header {
    flex-wrap: wrap;
  }
  
  .tab-btn {
    padding: 0.75rem 1rem;
    font-size: 0.8rem;
  }
  
  .timeline {
    flex-direction: column;
    gap: 1rem;
  }
  
  .timeline::before {
    top: 0;
    left: 1rem;
    right: auto;
    bottom: 0;
    width: 2px;
    height: auto;
    background: linear-gradient(to bottom, #e5e7eb 0%, #1e3a8a 50%, #e5e7eb 100%);
  }
  
  .timeline-item {
    flex-direction: row;
    align-items: flex-start;
    min-width: auto;
    padding-left: 2.5rem;
  }
  
  .timeline-marker {
    position: absolute;
    left: 0;
    top: 0.25rem;
    width: 1.5rem;
    height: 1.5rem;
    font-size: 0.7rem;
    margin-bottom: 0;
  }
  
  .timeline-content {
    padding: 0.75rem;
    text-align: left;
    min-height: auto;
  }
  
  .timeline-title {
    font-size: 0.9rem;
  }
}
</style>